import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";

import classNames from "classnames/bind";
import useModalContext from "common/providers/modal/useModalContext";
import UIIcon from "common/blocks/UIIcon";
import useDisableScroll from "hooks/useDisableScroll/useDisableScroll";
import IconButton from "../IconButton";

import styles from "./PopUp.module.scss";

const cx = classNames.bind(styles);

const PopUp = () => {
    const { usePopup } = useModalContext();
    const { popup, closePopup, popupContent } = usePopup();
    const popupRef = useRef<HTMLDivElement>(null);

    useDisableScroll(popup);

    const popupElement: HTMLDivElement | null =
        typeof document !== "undefined"
            ? (document.querySelector("#popup-root") as HTMLDivElement)
            : null;

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                closePopup();
            }
        };

        popupElement?.addEventListener("click", handleOutsideClick);

        return () => {
            popupElement?.removeEventListener("click", handleOutsideClick);
        };
    }, [closePopup, popupElement]);

    const ContentComponent = popupContent?.content?.component ?? null;
    const FooterComponent = popupContent?.content?.footerComponent ?? null;

    useEffect(() => {
        if (popup && popupElement) {
            window?.imichatwidget?.hide?.();
        } else {
            window?.imichatwidget?.show?.();
        }
    }, [popup, popupElement]);

    const headerSection = popupContent?.titleIcon ? (
        <div className={cx("heading")}>
            <div className={cx("iconAndGoBack")}>
                <UIIcon
                    icon={popupContent.titleIcon.icon}
                    className={cx(popupContent.titleIcon.className)}
                />
                <IconButton
                    onClick={() => closePopup(true)}
                    title="Close Modal"
                    aria-label="Close Modal"
                    icon="Close"
                    className="noMargin"
                />
            </div>
            <span className={cx("title")}>{popupContent?.title ?? ""}</span>
        </div>
    ) : (
        <div className={cx("heading", "heading-row")}>
            <div className={cx("goBackAndTitle")}>
                <span className={cx("title")}>{popupContent?.title ?? ""}</span>
            </div>
            <IconButton
                onClick={() => closePopup(true)}
                title="Close Modal"
                aria-label="Close Modal"
                icon="Close"
                className="noMargin"
            />
        </div>
    );

    if (popup && popupElement) {
        return (
            <>
                {ReactDOM.createPortal(
                    <div className={cx("modal")}>
                        <div className={cx("popup")} ref={popupRef}>
                            {headerSection}
                            <div
                                className={cx("content", {
                                    noScroll: popupContent?.noScroll ?? false,
                                    center: popupContent?.centerContent ?? false,
                                })}
                            >
                                <ContentComponent {...(popupContent?.content?.props || {})} />
                            </div>
                            {FooterComponent && (
                                <FooterComponent {...(popupContent?.content?.props || {})} />
                            )}
                        </div>
                    </div>,
                    popupElement
                )}
            </>
        );
    }

    return null;
};

export default PopUp;
